import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import WhatsAppAdmin from './WhatsAppAdmin';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPanel = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  
  // Login state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  
  // Data state
  const [stats, setStats] = useState(null);
  const [transporters, setTransporters] = useState([]);
  const [trips, setTrips] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [notifications, setNotifications] = useState([]);

  // Filter states
  const [tripFilter, setTripFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [transporterFilter, setTransporterFilter] = useState('all');

  useEffect(() => {
    const token = sessionStorage.getItem('adminToken');
    if (token) {
      setIsLoggedIn(true);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      fetchData();
    }
  }, [isLoggedIn]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const response = await axios.post(`${API_URL}/api/admin/login`, {
        username,
        password
      });
      sessionStorage.setItem('adminToken', response.data.token);
      // Keep the password in session so we can authenticate one-off
      // recovery operations (like the falsely-failed payment scan) without
      // re-prompting. Cleared on logout.
      sessionStorage.setItem('adminPwd', password);
      setIsLoggedIn(true);
    } catch (error) {
      setLoginError('Invalid credentials');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminToken');
    sessionStorage.removeItem('adminPwd');
    setIsLoggedIn(false);
  };

  const [recovering, setRecovering] = useState(false);
  const handleRecoverFalselyFailed = async () => {
    const adminPwd = sessionStorage.getItem('adminPwd');
    if (!adminPwd) {
      alert('Please log out and log back in so we can authenticate the recovery action.');
      return;
    }
    if (!window.confirm(
      'This will scan ALL payments where CMI approved the transaction at the bank ' +
      '(ProcReturnCode=00 + AuthCode populated) but our system marked them as "failed", ' +
      'and retroactively flip them to "paid". Customers + transporters will be notified. ' +
      'Continue?'
    )) return;
    setRecovering(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/payments/recover-falsely-failed`,
        null,
        { params: { admin_password: adminPwd } }
      );
      alert(
        `Recovery complete!\n\n` +
        `Repaired: ${res.data.repaired_count} payment${res.data.repaired_count !== 1 ? 's' : ''}\n\n` +
        (res.data.repaired_count > 0
          ? `Details:\n${res.data.repaired
              .map(p => `• ${p.payment_id} — MAD ${p.amount} — ${p.customer_email}`)
              .join('\n')}`
          : 'No falsely-failed payments found — your data is clean.')
      );
      // Refresh dashboard data so the new "paid" status shows up.
      fetchData();
    } catch (error) {
      alert('Recovery failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setRecovering(false);
    }
  };

  // --- Database Backup & Restore ------------------------------------------
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);

  const handleExportDb = async () => {
    const adminPwd = sessionStorage.getItem('adminPwd');
    if (!adminPwd) {
      alert('Please log out and log back in so we can authenticate the export.');
      return;
    }
    setExporting(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/db/export`, {
        params: { admin_password: adminPwd },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/gzip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '');
      a.download = `horsego_export_${ts}.json.gz`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Export failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setExporting(false);
    }
  };

  const handleImportDb = async (e) => {
    const fileInput = e.target;
    const file = fileInput.files && fileInput.files[0];
    if (!file) return;
    const adminPwd = sessionStorage.getItem('adminPwd');
    if (!adminPwd) {
      alert('Please log out and log back in so we can authenticate the import.');
      fileInput.value = '';
      return;
    }
    if (!window.confirm(
      `This will REPLACE the data in the current environment's database with the ` +
      `contents of "${file.name}". Existing records in matching collections will be ` +
      `wiped first. Continue?`
    )) {
      fileInput.value = '';
      return;
    }
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(
        `${API_URL}/api/admin/db/import`,
        formData,
        { params: { admin_password: adminPwd, mode: 'replace' } }
      );
      const cols = res.data.collections_imported || {};
      const detail = Object.entries(cols)
        .map(([k, v]) => `• ${k}: ${v}`)
        .join('\n');
      alert(
        `Import complete!\n\n` +
        `Total documents: ${res.data.total_documents}\n` +
        `Source DB: ${res.data.imported_from_db || 'n/a'}\n\n` +
        `${detail}`
      );
      fetchData();
    } catch (error) {
      alert('Import failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
      fileInput.value = '';
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, transportersRes, tripsRes, quotesRes, customersRes, transactionsRes, notificationsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`),
        axios.get(`${API_URL}/api/admin/transporters`),
        axios.get(`${API_URL}/api/admin/trips`),
        axios.get(`${API_URL}/api/admin/quotes`),
        axios.get(`${API_URL}/api/admin/customers`),
        axios.get(`${API_URL}/api/admin/transactions`),
        axios.get(`${API_URL}/api/admin/notifications`)
      ]);
      setStats(statsRes.data);
      setTransporters(transportersRes.data);
      setTrips(tripsRes.data);
      setQuotes(quotesRes.data);
      setCustomers(customersRes.data);
      setTransactions(transactionsRes.data);
      setNotifications(notificationsRes.data);
    } catch (error) {
      // Error handled silently in production
    } finally {
      setLoading(false);
    }
  };

  const updateTransporterStatus = async (userId, newStatus) => {
    try {
      await axios.patch(`${API_URL}/api/admin/transporters/${userId}/status`, {
        status: newStatus
      });
      fetchData();
    } catch (error) {
      // Silently handle in production
    }
  };

  const updateCustomerStatus = async (customerId, newStatus) => {
    try {
      await axios.patch(`${API_URL}/api/admin/customers/${customerId}/status`, {
        status: newStatus
      });
      fetchData();
    } catch (error) {
      // Silently handle in production
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-green-500/20 text-green-400',
      pending: 'bg-yellow-500/20 text-yellow-400',
      suspended: 'bg-red-500/20 text-red-400',
      confirmed: 'bg-green-500/20 text-green-400',
      quoted: 'bg-blue-500/20 text-blue-400',
      in_progress: 'bg-purple-500/20 text-purple-400',
      completed: 'bg-gray-500/20 text-gray-400',
      accepted: 'bg-green-500/20 text-green-400',
      rejected: 'bg-red-500/20 text-red-400',
      lost: 'bg-red-500/20 text-red-400'
    };
    return colors[status] || 'bg-gray-500/20 text-gray-400';
  };

  const filteredTrips = trips.filter(trip => {
    if (tripFilter === 'all') return true;
    return trip.status === tripFilter;
  });

  const filteredCustomers = customers.filter(customer => {
    if (customerFilter === 'all') return true;
    return customer.status === customerFilter;
  });

  const filteredTransporters = transporters.filter(transporter => {
    if (transporterFilter === 'all') return true;
    return transporter.status === transporterFilter;
  });

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <div className="bg-[#1a1a1a] rounded-2xl p-8 w-full max-w-md border border-white/10">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white">
              Horse<span className="text-amber-500">GO</span>
            </h1>
            <p className="text-gray-400 mt-2">Admin Panel</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            {loginError && (
              <div className="bg-red-500/20 text-red-400 p-3 rounded-lg text-sm">
                {loginError}
              </div>
            )}
            
            <div>
              <label className="block text-gray-400 text-sm mb-2">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-amber-500"
                placeholder="Enter username"
              />
            </div>
            
            <div>
              <label className="block text-gray-400 text-sm mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-amber-500"
                placeholder="Enter password"
              />
            </div>
            
            <button
              type="submit"
              className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold py-3 rounded-full transition-colors"
            >
              Login
            </button>
          </form>
          
          <button
            onClick={() => navigate('/')}
            className="w-full mt-4 text-gray-400 hover:text-white text-sm"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <header className="bg-[#1a1a1a] border-b border-white/10 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold text-white">
              Horse<span className="text-amber-500">GO</span>
            </h1>
            <span className="bg-amber-500/20 text-amber-400 px-3 py-1 rounded-full text-sm font-medium">
              Admin Panel
            </span>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={fetchData}
              className="text-gray-400 hover:text-white transition-colors"
              title="Refresh Data"
            >
              🔄 Refresh
            </button>
            <button
              onClick={handleLogout}
              className="bg-red-500/20 text-red-400 px-4 py-2 rounded-lg hover:bg-red-500/30 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-[#1a1a1a] min-h-[calc(100vh-73px)] border-r border-white/10 p-4">
          <nav className="space-y-2">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: '📊' },
              { id: 'transactions', label: 'Transactions', icon: '💳' },
              { id: 'trips', label: 'All Trips', icon: '📋' },
              { id: 'customers', label: 'Customers', icon: '👥' },
              { id: 'transporters', label: 'Transporters', icon: '🚚' },
              { id: 'quotes', label: 'All Quotes', icon: '💰' },
              { id: 'notifications', label: 'Notifications', icon: '🔔' },
              { id: 'whatsapp', label: 'WhatsApp', icon: '💬' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`admin-tab-${tab.id}`}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-amber-500/20 text-amber-400'
                    : 'text-gray-400 hover:bg-white/5 hover:text-white'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
            </div>
          ) : (
            <>
              {/* Dashboard Tab */}
              {activeTab === 'dashboard' && stats && (
                <div>
                  <h2 className="text-2xl font-bold text-white mb-6">Dashboard Overview</h2>
                  
                  {/* Stats Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    <StatCard label="Total Revenue" value={`MAD ${stats.total_revenue}`} icon="💰" color="green" />
                    <StatCard label="Total Trips" value={stats.total_trips} icon="📋" color="blue" />
                    <StatCard label="Total Transporters" value={stats.total_transporters} icon="🚚" color="amber" />
                    <StatCard label="Total Customers" value={stats.total_customers} icon="👥" color="purple" />
                  </div>

                  {/* Quick Links */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    <button 
                      onClick={() => setActiveTab('transactions')}
                      className="bg-[#1a1a1a] rounded-xl p-6 border border-white/10 hover:border-amber-500/50 transition-colors text-left"
                    >
                      <div className="text-3xl mb-2">💳</div>
                      <h3 className="text-white font-semibold">Transactions</h3>
                      <p className="text-gray-400 text-sm">{stats.accepted_quotes} completed</p>
                    </button>
                    <button 
                      onClick={() => setActiveTab('trips')}
                      className="bg-[#1a1a1a] rounded-xl p-6 border border-white/10 hover:border-amber-500/50 transition-colors text-left"
                    >
                      <div className="text-3xl mb-2">📋</div>
                      <h3 className="text-white font-semibold">All Trips</h3>
                      <p className="text-gray-400 text-sm">{stats.pending_trips} pending</p>
                    </button>
                    <button 
                      onClick={() => setActiveTab('customers')}
                      className="bg-[#1a1a1a] rounded-xl p-6 border border-white/10 hover:border-amber-500/50 transition-colors text-left"
                    >
                      <div className="text-3xl mb-2">👥</div>
                      <h3 className="text-white font-semibold">Customers</h3>
                      <p className="text-gray-400 text-sm">{stats.total_customers} registered</p>
                    </button>
                  </div>

                  {/* Trip & Quote Status */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-[#1a1a1a] rounded-xl p-6 border border-white/10">
                      <h3 className="text-lg font-semibold text-white mb-4">Trip Status</h3>
                      <div className="space-y-3">
                        <StatusBar label="Pending" value={stats.pending_trips} total={stats.total_trips} color="yellow" />
                        <StatusBar label="Quoted" value={stats.quoted_trips} total={stats.total_trips} color="blue" />
                        <StatusBar label="Confirmed" value={stats.confirmed_trips} total={stats.total_trips} color="green" />
                        <StatusBar label="In Progress" value={stats.in_progress_trips} total={stats.total_trips} color="purple" />
                        <StatusBar label="Completed" value={stats.completed_trips} total={stats.total_trips} color="gray" />
                      </div>
                    </div>

                    <div className="bg-[#1a1a1a] rounded-xl p-6 border border-white/10">
                      <h3 className="text-lg font-semibold text-white mb-4">Quote Status</h3>
                      <div className="space-y-3">
                        <StatusBar label="Pending Quotes" value={stats.pending_quotes} total={stats.total_quotes} color="yellow" />
                        <StatusBar label="Accepted Quotes" value={stats.accepted_quotes} total={stats.total_quotes} color="green" />
                      </div>
                      <div className="mt-4 pt-4 border-t border-white/10">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Active Transporters</span>
                          <span className="text-green-400 font-medium">{stats.active_transporters}</span>
                        </div>
                        <div className="flex justify-between text-sm mt-2">
                          <span className="text-gray-400">Pending Approval</span>
                          <span className="text-yellow-400 font-medium">{stats.pending_transporters}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Database Backup & Restore */}
                  <div className="mt-6 bg-[#1a1a1a] rounded-xl border border-white/10 p-6">
                    <h3 className="text-lg font-bold text-white mb-1">Database Backup &amp; Restore</h3>
                    <p className="text-gray-400 text-sm mb-4">
                      Export the full database as a file (run this on the live site to grab real data),
                      then import that file in another environment to copy the data across.
                    </p>
                    <div className="flex items-center gap-3 flex-wrap">
                      <button
                        onClick={handleExportDb}
                        disabled={exporting}
                        data-testid="db-export-btn"
                        className="bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold px-4 py-2 rounded-lg text-sm transition-colors"
                        title="Download the entire database as a gzipped JSON file."
                      >
                        {exporting ? 'Exporting…' : '⬇️ Export Database'}
                      </button>

                      <label
                        className={`bg-purple-500 hover:bg-purple-600 text-white font-semibold px-4 py-2 rounded-lg text-sm transition-colors cursor-pointer ${importing ? 'opacity-50 pointer-events-none' : ''}`}
                        title="Import a previously exported database file into THIS environment (replaces matching collections)."
                      >
                        {importing ? 'Importing…' : '⬆️ Import Database'}
                        <input
                          type="file"
                          accept=".gz,.json"
                          onChange={handleImportDb}
                          data-testid="db-import-input"
                          className="hidden"
                          disabled={importing}
                        />
                      </label>
                    </div>
                    <p className="text-amber-400/80 text-xs mt-3">
                      ⚠️ Import uses REPLACE mode — collections in the file overwrite the current data.
                    </p>
                  </div>
                </div>
              )}

              {/* Transactions Tab */}
              {activeTab === 'transactions' && (
                <div>
                  <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                    <h2 className="text-2xl font-bold text-white">All Transactions</h2>
                    <div className="flex items-center gap-3 flex-wrap">
                      <button
                        onClick={handleRecoverFalselyFailed}
                        disabled={recovering}
                        data-testid="recover-falsely-failed-btn"
                        className="bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed text-black font-semibold px-4 py-2 rounded-lg text-sm transition-colors"
                        title="Scan and repair payments that CMI approved at the bank but our system wrongly marked as failed."
                      >
                        {recovering ? 'Recovering…' : '🔧 Recover Falsely-Failed Payments'}
                      </button>
                      <span className="bg-green-500/20 text-green-400 px-4 py-2 rounded-lg">
                        {transactions.length} Total | MAD {transactions.reduce((sum, t) => sum + (t.customer_total || 0), 0).toFixed(2)} Revenue
                      </span>
                    </div>
                  </div>
                  
                  <div className="bg-[#1a1a1a] rounded-xl border border-white/10 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-white/5">
                          <tr>
                            <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Quote ID</th>
                            <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Customer</th>
                            <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Transporter</th>
                            <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Route</th>
                            <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Customer Paid (Gross)</th>
                            <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Commission (10%)</th>
                            <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Date</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                          {transactions.map((tx) => (
                            <tr key={tx.quote_id} className="hover:bg-white/5">
                              <td className="px-6 py-4 text-amber-400 font-mono text-sm">{tx.quote_id}</td>
                              <td className="px-6 py-4">
                                <div className="text-white">{tx.customer_name}</div>
                                <div className="text-gray-500 text-xs">{tx.customer_email}</div>
                              </td>
                              <td className="px-6 py-4 text-white">{tx.transporter_company}</td>
                              <td className="px-6 py-4">
                                <div className="text-gray-400 text-xs max-w-[200px]">
                                  <div className="truncate">📍 {tx.pickup}</div>
                                  <div className="truncate">📍 {tx.dropoff}</div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <span className="text-green-400 font-bold">MAD {tx.customer_total?.toFixed(2)}</span>
                              </td>
                              <td className="px-6 py-4">
                                <span className="text-amber-400">MAD {tx.commission_total?.toFixed(2)}</span>
                              </td>
                              <td className="px-6 py-4 text-gray-400 text-sm">
                                {tx.created_at ? new Date(tx.created_at).toLocaleDateString() : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {transactions.length === 0 && (
                      <div className="text-center py-12 text-gray-500">
                        No transactions yet
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Trips Tab */}
              {activeTab === 'trips' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-white">All Trip Requests</h2>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400 text-sm">Filter:</span>
                      {['all', 'pending', 'quoted', 'confirmed', 'in_progress', 'completed'].map((filter) => (
                        <button
                          key={filter}
                          onClick={() => setTripFilter(filter)}
                          className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                            tripFilter === filter
                              ? 'bg-amber-500 text-black'
                              : 'bg-white/10 text-gray-400 hover:bg-white/20'
                          }`}
                        >
                          {filter.charAt(0).toUpperCase() + filter.slice(1).replace('_', ' ')}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div className="bg-[#1a1a1a] rounded-xl border border-white/10 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-white/5">
                          <tr>
                            <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Request ID</th>
                            <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Customer</th>
                            <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Contact</th>
                            <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Route</th>
                            <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Date</th>
                            <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Horses</th>
                            <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Status</th>
                            <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Created</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                          {filteredTrips.map((trip) => (
                            <tr key={trip.request_id} className="hover:bg-white/5">
                              <td className="px-6 py-4 text-amber-400 font-mono text-sm">{trip.request_id}</td>
                              <td className="px-6 py-4">
                                <div className="text-white">{trip.first_name} {trip.last_name}</div>
                                <div className="text-gray-500 text-xs">{trip.email}</div>
                              </td>
                              <td className="px-6 py-4 text-gray-400 text-sm">{trip.contact_number}</td>
                              <td className="px-6 py-4">
                                <div className="text-gray-400 text-sm max-w-[250px]">
                                  <div className="flex items-center gap-2">
                                    <span className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></span>
                                    <span className="truncate">{trip.pickup_address}</span>
                                  </div>
                                  <div className="flex items-center gap-2 mt-1">
                                    <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0"></span>
                                    <span className="truncate">{trip.dropoff_address}</span>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-gray-400">{trip.transport_date}</td>
                              <td className="px-6 py-4 text-white text-center">{trip.horse_count}</td>
                              <td className="px-6 py-4">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(trip.status)}`}>
                                  {trip.status?.toUpperCase().replace('_', ' ')}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-gray-500 text-sm">
                                {trip.created_at ? new Date(trip.created_at).toLocaleDateString() : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {filteredTrips.length === 0 && (
                      <div className="text-center py-12 text-gray-500">
                        No trips found with selected filter
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Customers Tab */}
              {activeTab === 'customers' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-white">All Customers</h2>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400 text-sm">Filter:</span>
                      {['all', 'active', 'suspended'].map((filter) => (
                        <button
                          key={filter}
                          onClick={() => setCustomerFilter(filter)}
                          className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                            customerFilter === filter
                              ? 'bg-amber-500 text-black'
                              : 'bg-white/10 text-gray-400 hover:bg-white/20'
                          }`}
                        >
                          {filter.charAt(0).toUpperCase() + filter.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div className="bg-[#1a1a1a] rounded-xl border border-white/10 overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-white/5">
                        <tr>
                          <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Name</th>
                          <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Email</th>
                          <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Phone</th>
                          <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">City</th>
                          <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Status</th>
                          <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Registered</th>
                          <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {filteredCustomers.map((customer) => (
                          <tr key={customer.customer_id} className="hover:bg-white/5">
                            <td className="px-6 py-4 text-white font-medium">{customer.first_name} {customer.last_name}</td>
                            <td className="px-6 py-4 text-amber-400">{customer.email}</td>
                            <td className="px-6 py-4 text-gray-400">{customer.phone}</td>
                            <td className="px-6 py-4 text-gray-400">{customer.city || '-'}</td>
                            <td className="px-6 py-4">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(customer.status || 'active')}`}>
                                {(customer.status || 'active').toUpperCase()}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-gray-500 text-sm">
                              {customer.created_at ? new Date(customer.created_at).toLocaleDateString() : '-'}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex gap-2">
                                {customer.status !== 'active' && (
                                  <button
                                    onClick={() => updateCustomerStatus(customer.customer_id, 'active')}
                                    className="bg-green-500/20 text-green-400 px-3 py-1 rounded text-xs hover:bg-green-500/30"
                                  >
                                    Activate
                                  </button>
                                )}
                                {customer.status !== 'suspended' && (
                                  <button
                                    onClick={() => updateCustomerStatus(customer.customer_id, 'suspended')}
                                    className="bg-red-500/20 text-red-400 px-3 py-1 rounded text-xs hover:bg-red-500/30"
                                  >
                                    Suspend
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {filteredCustomers.length === 0 && (
                      <div className="text-center py-12 text-gray-500">
                        No customers found
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Transporters Tab */}
              {activeTab === 'transporters' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-white">All Transporters</h2>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400 text-sm">Filter:</span>
                      {['all', 'active', 'pending', 'suspended'].map((filter) => (
                        <button
                          key={filter}
                          onClick={() => setTransporterFilter(filter)}
                          className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                            transporterFilter === filter
                              ? 'bg-amber-500 text-black'
                              : 'bg-white/10 text-gray-400 hover:bg-white/20'
                          }`}
                        >
                          {filter.charAt(0).toUpperCase() + filter.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div className="bg-[#1a1a1a] rounded-xl border border-white/10 overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-white/5">
                        <tr>
                          <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Company</th>
                          <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Contact Person</th>
                          <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Email</th>
                          <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Phone</th>
                          <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">City</th>
                          <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Status</th>
                          <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Registered</th>
                          <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {filteredTransporters.map((t) => (
                          <tr key={t.user_id} className="hover:bg-white/5">
                            <td className="px-6 py-4 text-white font-medium">{t.company_name}</td>
                            <td className="px-6 py-4 text-gray-300">{t.contact_person}</td>
                            <td className="px-6 py-4 text-amber-400">{t.email}</td>
                            <td className="px-6 py-4 text-gray-400">{t.phone || '-'}</td>
                            <td className="px-6 py-4 text-gray-400">{t.city || '-'}</td>
                            <td className="px-6 py-4">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(t.status)}`}>
                                {t.status?.toUpperCase()}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-gray-500 text-sm">
                              {t.created_at ? new Date(t.created_at).toLocaleDateString() : '-'}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex gap-2">
                                {t.status !== 'active' && (
                                  <button
                                    onClick={() => updateTransporterStatus(t.user_id, 'active')}
                                    className="bg-green-500/20 text-green-400 px-3 py-1 rounded text-xs hover:bg-green-500/30"
                                  >
                                    Activate
                                  </button>
                                )}
                                {t.status !== 'suspended' && (
                                  <button
                                    onClick={() => updateTransporterStatus(t.user_id, 'suspended')}
                                    className="bg-red-500/20 text-red-400 px-3 py-1 rounded text-xs hover:bg-red-500/30"
                                  >
                                    Suspend
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {filteredTransporters.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        No transporters found
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Quotes Tab */}
              {activeTab === 'quotes' && (
                <div>
                  <h2 className="text-2xl font-bold text-white mb-6">All Quotes</h2>
                  <div className="bg-[#1a1a1a] rounded-xl border border-white/10 overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-white/5">
                        <tr>
                          <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Quote ID</th>
                          <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Trip ID</th>
                          <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Transporter</th>
                          <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Price</th>
                          <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Platform Fee</th>
                          <th className="text-left text-gray-400 px-6 py-4 text-sm font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {quotes.map((quote) => (
                          <tr key={quote.quote_id} className="hover:bg-white/5">
                            <td className="px-6 py-4 text-amber-400 font-mono text-sm">{quote.quote_id}</td>
                            <td className="px-6 py-4 text-gray-400 font-mono text-sm">{quote.trip_request_id}</td>
                            <td className="px-6 py-4 text-white">{quote.transporter_company}</td>
                            <td className="px-6 py-4">
                              <div className="text-white font-medium">MAD {quote.customer_total?.toFixed(2)}</div>
                              <div className="text-gray-500 text-xs">Transport: MAD {quote.transport_price?.toFixed(2)}</div>
                            </td>
                            <td className="px-6 py-4 text-green-400">MAD {quote.commission_total?.toFixed(2)}</td>
                            <td className="px-6 py-4">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(quote.status)}`}>
                                {quote.status?.toUpperCase()}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Notifications Tab */}
              {activeTab === 'notifications' && (
                <div>
                  <h2 className="text-2xl font-bold text-white mb-6">All Notifications</h2>
                  <div className="space-y-3">
                    {notifications.map((notif) => (
                      <div
                        key={notif.notification_id}
                        className={`bg-[#1a1a1a] rounded-xl p-4 border ${
                          notif.is_read ? 'border-white/5' : 'border-amber-500/30'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                notif.user_type === 'customer' ? 'bg-blue-500/20 text-blue-400' : 
                                notif.user_type === 'transporter' ? 'bg-green-500/20 text-green-400' : 
                                'bg-purple-500/20 text-purple-400'
                              }`}>
                                {notif.user_type}
                              </span>
                              <span className="text-amber-400 text-sm font-medium">{notif.user_display || notif.user_email || notif.user_id}</span>
                            </div>
                            <h4 className="text-white font-medium mt-2">{notif.title}</h4>
                            <p className="text-gray-400 text-sm mt-1">{notif.message}</p>
                          </div>
                          <div className="text-right">
                            <span className="text-gray-500 text-xs">
                              {new Date(notif.created_at).toLocaleString()}
                            </span>
                            {!notif.is_read && (
                              <span className="block mt-1 text-amber-400 text-xs">● Unread</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    {notifications.length === 0 && (
                      <div className="text-center py-12 text-gray-500 bg-[#1a1a1a] rounded-xl">
                        No notifications yet
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* WhatsApp Tab */}
              {activeTab === 'whatsapp' && (
                <WhatsAppAdmin />
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

// Helper Components
const StatCard = ({ label, value, icon, color }) => {
  const colors = {
    green: 'bg-green-500/20 text-green-400',
    blue: 'bg-blue-500/20 text-blue-400',
    amber: 'bg-amber-500/20 text-amber-400',
    purple: 'bg-purple-500/20 text-purple-400'
  };
  
  return (
    <div className="bg-[#1a1a1a] rounded-xl p-6 border border-white/10">
      <div className={`w-12 h-12 rounded-lg ${colors[color]} flex items-center justify-center text-2xl mb-4`}>
        {icon}
      </div>
      <div className="text-3xl font-bold text-white">{value}</div>
      <div className="text-gray-400 text-sm mt-1">{label}</div>
    </div>
  );
};

const StatusBar = ({ label, value, total, color }) => {
  const percentage = total > 0 ? (value / total) * 100 : 0;
  const colors = {
    yellow: 'bg-yellow-500',
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    purple: 'bg-purple-500',
    gray: 'bg-gray-500'
  };
  
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-400">{label}</span>
        <span className="text-white font-medium">{value}</span>
      </div>
      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
        <div 
          className={`h-full ${colors[color]} rounded-full transition-all`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

export default AdminPanel;
